<template>
  <section class="mb-6 space-y-4">
    <AncillaryBaggagePageOpenButton
      :is-loading="ancillariesStore.isAncillariesLoading"
      :total-price="ancillariesStore.passengersWithBaggage.totalPrice"
      :has-selected-baggage="
        Boolean(ancillariesStore.passengersWithBaggage.data?.hasSelectedBaggage)
      "
      :is-baggage-available="ancillariesStore.isBaggageAvailable"
      :draft-passengers-with-baggages="
        ancillariesStore.passengersWithBaggage.data?.passengers || []
      "
      @onProceedToBaggages="onProceedToBaggages"
    />
    <SeatMapButton
      v-if="isSeatMapAvailable"
      :offer-id="offerId"
      :is-loading="seatMapCheckQuery.isLoading.value"
      :seat-map-state-by-segment="bookingStore.state.seatMap"
      :mapped-flights="bookingStore.mappedFlights"
      :passengers-data="bookingStore.state.passengers"
      @onProceedToSeatMap="onProceedToSeatMap"
    />
    <div v-if="products.roamingProduct.value" class="relative pt-3">
      <FreeLabel class="absolute left-4 top-0 w-max" />
      <RoamingButton
        :roaming-product="products.roamingProduct.value"
        :is-selected="products.isRoamingSelected.value"
        has-label
        @onShowRoamingInfo="onShowRoamingInfo"
        @onProductToggle="products.toggleRoamingProduct"
      />
      <RoamingDescriptionBottomSheet
        :roaming-product="products.roamingProduct.value"
        :products-state="products.state.value"
        :is-selected="products.isRoamingSelected.value"
        @onOpenDevices="onOpenRoamingDevices"
        @onProductToggle="products.toggleRoamingProduct"
      />
      <RoamingDevicesBottomSheet />
    </div>
    <div v-if="products.flightDelayProducts.value?.length" class="w-full">
      <FlightDelayButton
        :flight-delay-products="products.flightDelayProducts.value"
      />
      <FlightDelayFormBottomSheet
        :name="flightDelayConfig.FLIGHT_DELAY_FORM_BOTTOM_SHEET"
        :flights="bookingStore.mappedFlights"
        :passengers="bookingStore.passengersSaveResponse"
        :offer-id="offerId"
        :flight-delay-products="products.flightDelayProducts.value"
      />
    </div>
    <div
      v-if="products.medicalInsuranceProducts.value.length > 0"
      class="w-full"
    >
      <MedicalInsuranceButton
        :has-error="hasMedicalInsuranceValidationError"
        :medical-insurance-products="
          products.medicalInsuranceProducts.value || []
        "
        @onOpenMedicalInsuranceBottomSheet="openMedicalInsuranceBottomSheet"
      />
      <MedicalInsuranceFormBottomSheet
        :name="medicalInsuranceConfig.FORM_BOTTOM_SHEET"
        :offer-id="offerId"
        :is-one-way="isOneWayMedicalInsurance"
        @onUpdateProducts="bookingStore.updateMedicalInsuranceProducts"
      />
    </div>
    <PublicOfferBottomSheet
      :name="medicalInsuranceConfig.PUBLIC_OFFER_BOTTOM_SHEET"
      :fetch-public-offer="publicOfferApi.fetchPublicOffer"
    />
  </section>
</template>
<script setup lang="ts">
  import type { Ref } from 'vue';
  import { computed, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    FreeLabel,
    PublicOfferBottomSheet,
    RoamingDevicesBottomSheet,
  } from 'freedom-shared';
  import { controllerModel, hashCodeLib } from 'shared';
  import { storeToRefs } from 'pinia';
  import useVuelidate from '@vuelidate/core';

  import {
    useMedicalInsuranceStore,
    MedicalInsuranceFormBottomSheet,
    MedicalInsuranceButton,
    medicalInsuranceConfig,
  } from '@/widgets/medical-insurance';
  import {
    AncillaryBaggagePageOpenButton,
    ancillaryBaggageModel,
  } from '@/widgets/ancillary-baggage';
  import {
    RoamingDescriptionBottomSheet,
    roamingConfig,
  } from '@/widgets/roaming';
  import {
    flightDelayConfig,
    FlightDelayFormBottomSheet,
  } from '@/widgets/flight-delay';

  import { bookingStateModel } from '@/features/booking-state';

  import { publicOfferApi } from '@/entities/public-offer';

  import { SeatMapButton } from '../../seat-map';
  import { RoamingButton } from '../../roaming';
  import { FlightDelayButton } from '../../flight-delay';
  import type { UseProductsStateReturn } from '../types';

  const props = defineProps<{
    offerId: string;
    products: UseProductsStateReturn;
    seatMapCheckQuery: {
      data: Ref<{ available?: boolean } | undefined>;
      isLoading: Ref<boolean>;
    };
  }>();

  const router = useRouter();

  const bookingStore = bookingStateModel.useBookingStore();
  const ancillariesStore = ancillaryBaggageModel.useAncillariesStore();

  const vuelidate = useVuelidate();

  const bottomSheet = controllerModel.useBottomSheet();

  const medicalInsuranceStore = useMedicalInsuranceStore();

  /** Classic OW only — RT and multi-city use return / multi-leg insurance rules. */
  const isOneWayMedicalInsurance = computed(() => {
    const flightsLen = bookingStore.offer?.flights.length ?? 0;
    const query = bookingStore.offer?.$meta?.search_query;
    const isMt =
      typeof query === 'string' && hashCodeLib.isMultiTripSearch(query);
    return flightsLen === 1 && !isMt;
  });

  const { passengersSaveResponse } = storeToRefs(bookingStore);

  const isSeatMapAvailable = computed(
    () =>
      !!props.seatMapCheckQuery.data.value?.available ||
      props.seatMapCheckQuery.isLoading.value,
  );

  watch(
    () => [
      passengersSaveResponse.value,
      props.products.medicalInsuranceProducts.value,
    ],
    ([passengers, products]) => {
      medicalInsuranceStore.setPassengersSaveResponse(passengers);
      medicalInsuranceStore.setMedicalInsuranceProducts(products);
    },
    { immediate: true },
  );

  function updateProductsStateAtBookingStore() {
    const productsState = {
      products: props.products.products.value,
      state: props.products.state.value,
    };

    bookingStore.update({
      ancillaries: ancillariesStore.ancillaries?.state,
      products: productsState,
      packageInfo: props.products.packageInfo.value,
      roaming: props.products.isRoamingSelected.value
        ? props.products.products.value.filter(
            product => product.service === roamingConfig.ROAMING,
          )
        : [],
    });
  }

  function onProceedToBaggages() {
    // we have to save draft products state to global state for avoid losing of draft state when we leave the page
    updateProductsStateAtBookingStore();
    router.push({ name: 'AncillaryBaggagePage' });
  }
  function onProceedToSeatMap() {
    // we have to save draft products state to global state for avoid losing of draft state when we leave the page
    updateProductsStateAtBookingStore();
    router.push({ name: 'SeatMapPage' });
  }
  function openMedicalInsuranceBottomSheet() {
    bottomSheet.show(medicalInsuranceConfig.FORM_BOTTOM_SHEET);
  }

  function onShowRoamingInfo() {
    bottomSheet.show(roamingConfig.INFO_BOTTOM_SHEET);
  }

  function onOpenRoamingDevices() {
    bottomSheet.show(roamingConfig.DEVICES_BOTTOM_SHEET);
  }

  const hasMedicalInsuranceValidationError = computed(() => {
    return Boolean(
      vuelidate.value.$errors.find(err => {
        return err.$property === 'passportDate';
      }),
    );
  });
</script>
